import {
  useCheckPdfStatus,
  useDownloadPdf,
  useInitiatePdfGeneration,
  webDownload,
} from "creso-core/hooks";
import { getPromiseWithResolvers, sleep } from "creso-core/utils";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "web-design-system";
import { handleError } from "../utils/error";

class TaskCancelledError extends Error {
  constructor() {
    super("Task cancelled");
    this.name = "TaskCancelledError";
  }
}

const MAX_ATTEMPTS = 10;

export function Loader({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm">{title}</span>
      <span className="text-sm text-muted-foreground">{description}</span>
    </div>
  );
}

export function useDownloadPdfReport(downloader = webDownload) {
  const initiatePDFGeneration = useInitiatePdfGeneration();

  const [isGenerating, setIsGenerating] = useState(false);

  const checkPdfStatus = useCheckPdfStatus();

  const downloadMutation = useDownloadPdf();

  const pollingTask = useRef<ReturnType<typeof getPromiseWithResolvers> | null>(
    null
  );

  const pollPdfStatus = useCallback(
    async (taskId: string, attempt = 0) => {
      if (attempt >= MAX_ATTEMPTS) {
        throw new Error("PDF generation timed out");
      }
      const status = await checkPdfStatus.mutateAsync(taskId);
      if (status.status === "SUCCESS") {
        if (status.result?.status === "completed") return status;
        if (status.result?.status === "failed")
          throw new Error("PDF generation failed");
      }
      await sleep(3000);
      return pollPdfStatus(taskId, attempt + 1);
    },
    [checkPdfStatus]
  );

  const cancel = useCallback(() => {
    if (!pollingTask.current) return;
    pollingTask.current.reject(new TaskCancelledError());
    pollingTask.current = null;
  }, []);

  const generate = useCallback(
    async (
      downloadUrl: string,
      fileName: string,
      options: {
        loading?: string;
        dimensions?: { width: number; height: number };
        hideToast?: boolean;
      } = {}
    ) => {
      try {
        setIsGenerating(true);
        const taskId = await initiatePDFGeneration.mutateAsync({
          url: downloadUrl,
          dimensions: options?.dimensions,
        });
        pollingTask.current = getPromiseWithResolvers();
        const contest = Promise.race([
          pollPdfStatus(taskId.task_id),
          pollingTask.current.promise,
        ]);
        if (options.hideToast === true) {
          await contest;
        } else {
          await toast
            .promise(contest, {
              loading: (
                <Loader
                  title={options?.loading ?? "Generating report"}
                  description="This may take upto 10 seconds"
                />
              ),
              action: (
                <Button
                  className="ml-auto"
                  onClick={cancel}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              ),
            })
            .unwrap();
        }
        pollingTask.current.resolve(true);
        pollingTask.current = null;
        const pdfBlob = await downloadMutation.mutateAsync(taskId.task_id);
        await downloader(pdfBlob, fileName);
        return pdfBlob;
      } catch (error) {
        // Caller owns errors when suppressing the built-in toast (e.g. batch/zip)
        if (options.hideToast) throw error;
        if (!(error instanceof TaskCancelledError))
          handleError(error, "Failed to download report");
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [downloadMutation, pollPdfStatus, initiatePDFGeneration, cancel, downloader]
  );

  return [generate, isGenerating, cancel] as const;
}
